import { memo, FunctionComponent } from 'react';
import map from 'lodash/map';
import size from 'lodash/size';
import isNil from 'lodash/isNil';
import toSafeInteger from 'lodash/toSafeInteger';
// Material UI imports
import CircularProgress from '@mui/material/CircularProgress';
// Skillmore UI Components
import GridBox from '@empathco/ui-components/src/mixins/GridBox';
import FetchFailedAlert from '@empathco/ui-components/src/elements/FetchFailedAlert';
import InlineAlert from '@empathco/ui-components/src/elements/InlineAlert';
import LoadingPlaceholder from '@empathco/ui-components/src/elements/LoadingPlaceholder';
import CardDeck from '@empathco/ui-components/src/elements/CardDeck';
// local imports
import { SkillActivity } from '../graphql/types';
import {
  DevPlanAdvisor, DevPlanCompletedAdvisor, EmployeeDevPlanAdvisor,
  DevPlanCourse, EmployeeDevPlanCourse, DevPlanCompletedCourse,
  DevPlanOpportunity, EmployeeDevPlanOpportunity, DevPlanCompletedOpportunity
} from '../graphql/customTypes';
import useCustomerSettings from '../config/customer';
import DevPlanCourseCard from './DevPlanCourseCard';
import EmployeeCard from './EmployeeCard';
import OpportunityCard from './OpportunityCard';
import ActivityCard from './ActivityCard';

type CoursesAndAdvisorsGridProps = {
  readonly courses?: (DevPlanCourse | EmployeeDevPlanCourse | DevPlanCompletedCourse)[] | null;
  readonly advisors?: (DevPlanAdvisor | EmployeeDevPlanAdvisor | DevPlanCompletedAdvisor)[] | null;
  readonly opportunities?: (DevPlanOpportunity | EmployeeDevPlanOpportunity | DevPlanCompletedOpportunity)[] | null;
  readonly activities?: SkillActivity[] | null;
  readonly pendingCourses?: boolean | null;
  readonly pendingAdvisors?: boolean | null;
  readonly pendingOpportunities?: boolean | null;
  readonly pendingActivities?: boolean | null;
  readonly failedCourses?: boolean | null;
  readonly failedAdvisors?: boolean | null;
  readonly failedOpportunities?: boolean | null;
  readonly failedActivities?: boolean | null;
  readonly disabled?: boolean | null;
  readonly notFoundMessage?: string;
  readonly isSelected?: boolean;
  readonly withoutLoadingPlaceholders?: boolean;
  readonly withCompletedState?: boolean;
  readonly withEmployeeControls?: boolean;
  readonly isMyPlan?: boolean;
  readonly onSelectCourse?: (item: DevPlanCourse) => void;
  readonly onSelectAdvisor?: (item: DevPlanAdvisor) => void;
  readonly onSelectOpportunity?: (item: DevPlanOpportunity) => void;
  readonly onSelectActivity?: (item: SkillActivity) => void;
  readonly onRemoveCourse?: (item: DevPlanCourse) => void;
  readonly onRemoveAdvisor?: (item: DevPlanAdvisor) => unknown;
  readonly onRemoveOpportunity?: (item: DevPlanOpportunity) => void;
  readonly onRemoveActivity?: (item: SkillActivity) => void;
  readonly onCompleteCourse?: (item: DevPlanCourse) => void;
  readonly onCompleteActivity?: (item: SkillActivity) => void;
  readonly onCompleteAdvisor?: (item: DevPlanAdvisor) => void;
  readonly onRequestAdvisor?: (id: number) => unknown;
  readonly courseCompletingId?: number;
  readonly activitySelectingId?: number;
  readonly activityCompletingId?: number;
  readonly advisorCompletingId?: number;
  readonly advisorRequestingId?: number;
  readonly courseHighlighRating?: number;
  readonly advisorHighlightCount?: number;
}

const CoursesAndAdvisorsGrid: FunctionComponent<CoursesAndAdvisorsGridProps> = ({
  courses,
  advisors,
  opportunities,
  activities,
  pendingCourses = false,
  pendingAdvisors = false,
  pendingOpportunities = false,
  pendingActivities = false,
  failedCourses = false,
  failedAdvisors = false,
  failedOpportunities = false,
  failedActivities = false,
  disabled: parentDisabled = false,
  notFoundMessage,
  isSelected,
  withoutLoadingPlaceholders = false,
  withCompletedState = false,
  withEmployeeControls = false,
  isMyPlan = false,
  onSelectCourse,
  onSelectAdvisor,
  onSelectOpportunity,
  onSelectActivity,
  onRemoveCourse,
  onRemoveAdvisor,
  onRemoveOpportunity,
  onRemoveActivity,
  onCompleteCourse,
  onCompleteActivity,
  onCompleteAdvisor,
  onRequestAdvisor,
  courseCompletingId,
  activitySelectingId,
  activityCompletingId,
  advisorCompletingId,
  advisorRequestingId,
  courseHighlighRating,
  advisorHighlightCount
  // eslint-disable-next-line complexity
}) => {
  const { HAS_COURSES, HAS_MENTORING, HAS_DEV_PLAN_OPPORTUNITIES } = useCustomerSettings();
  const disabled = parentDisabled || (withoutLoadingPlaceholders && (
    (!failedCourses && pendingCourses) ||
    (!failedAdvisors && pendingAdvisors) ||
    (!failedOpportunities && pendingOpportunities) ||
    (!failedActivities && pendingActivities)
  ));
  return (
    (!HAS_COURSES || failedCourses) &&
    (!HAS_MENTORING || failedAdvisors) &&
    (!HAS_DEV_PLAN_OPPORTUNITIES || failedOpportunities) &&
      <FetchFailedAlert flat/>
  ) || (
    (!HAS_COURSES || (pendingCourses && !courses)) &&
    (!HAS_MENTORING || (pendingAdvisors && !advisors)) &&
    (!HAS_DEV_PLAN_OPPORTUNITIES || (pendingOpportunities && !opportunities)) &&
      <LoadingPlaceholder flat/>
  ) || (
    (!HAS_COURSES || size(courses) < 1) &&
    (!HAS_MENTORING || size(advisors) < 1) &&
    (!HAS_DEV_PLAN_OPPORTUNITIES || size(opportunities) < 1) &&
    size(activities) < 1 && (
      <InlineAlert severity="info" message={notFoundMessage || 'hr.dev_plan.nothing_found'} flat/>
  )) || (
    <>
      {(failedCourses && <FetchFailedAlert flat/>) ||
      (pendingCourses && !withoutLoadingPlaceholders && <LoadingPlaceholder flat/>)}
      <CardDeck compact={withCompletedState}>
        {map(courses, (course) => (
          <DevPlanCourseCard
              key={course.id}
              item={course}
              disabled={disabled}
              isSelected={isSelected}
              isHighlighted={isNil(courseHighlighRating) ? undefined
                : courseHighlighRating === toSafeInteger(course.preferred_rating)}
              onSelect={onSelectCourse}
              onRemove={onRemoveCourse}
              onComplete={onCompleteCourse}
              onCompletePending={courseCompletingId === course.id}
              withCompletedState={withCompletedState}
          />
        ))}
        {map(advisors, (advisor) => (
          <EmployeeCard
              key={advisor.id}
              withSkills
              avatarVariant="devplanadvisor"
              item={advisor}
              disabled={disabled}
              isSelected={isSelected}
              isHighlighted={isNil(advisorHighlightCount) ? undefined
                : advisorHighlightCount === toSafeInteger(advisor.active_mentor_count)}
              onSelect={onSelectAdvisor}
              onRemove={onRemoveAdvisor}
              onComplete={onCompleteAdvisor}
              withCompletedState={withCompletedState}
              onCompletePendingId={advisorCompletingId}
              onAction={onRequestAdvisor}
              actionLabel={onRequestAdvisor ? 'skill.advisors.button.request' : undefined}
              actionFinishedLabel={onRequestAdvisor ? 'skill.advisors.button.requested' : undefined}
              actionPendingId={advisorRequestingId}
          />
        ))}
        {map(opportunities, (opportunity) => (
          <OpportunityCard
              key={opportunity.id}
              devplan
              item={opportunity}
              disabled={disabled}
              isSelected={isSelected}
              isMyPlan={isMyPlan}
              onSelect={onSelectOpportunity}
              onRemove={onRemoveOpportunity}
              withCompletedState={withCompletedState}
          />
        ))}
        {map(activities, (activity) => (
          <ActivityCard
              key={activity.id}
              item={activity}
              withEmployeeControls={withEmployeeControls}
              onSelect={onSelectActivity}
              onRemove={onRemoveActivity}
              onComplete={onCompleteActivity}
              disabled={disabled}
              isSelected={isSelected}
              withCompletedState={withCompletedState}
              onSelectPendingId={activitySelectingId}
              onCompletePendingId={activityCompletingId}
          />
        ))}
        {withoutLoadingPlaceholders && (
          (!failedAdvisors && pendingAdvisors) ||
          (!failedCourses && pendingCourses) ||
          (!failedOpportunities && pendingOpportunities) ||
          (!failedActivities && pendingActivities)
        ) ? (
          <GridBox
              container
              size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
              py={8}
              display="flex"
              alignItems="center"
              justifyContent="center"
          >
            <CircularProgress/>
          </GridBox>
        ) : undefined}
      </CardDeck>
      {((failedAdvisors || failedOpportunities || failedActivities) && <FetchFailedAlert flat/>) ||
      (!withoutLoadingPlaceholders && (pendingAdvisors || pendingOpportunities || pendingActivities) && (
        <LoadingPlaceholder flat/>
      ))}
    </>
  );
};

export default memo(CoursesAndAdvisorsGrid);
